import React, { createContext, useState, useEffect, useRef } from 'react';
import WebSocketService from './websocketService';
import { useNavigate } from 'react-router-dom';
import { apiAppFetch } from './utils/apiFetch';

const WebSocketContext = createContext();

const WebSocketProvider = ({ user_id, children, resource, stockIDs }) => {
  const [stocks, setStocks] = useState({});
  const [lastUpdateTime, setLastUpdateTime] = useState(null);
  const [trades, setTrades] = useState([]);
  const [positions, setPortfolio] = useState([]);
  const [netPositions, setNetPositions] = useState([]);
  const [summary, setSummary] = useState({});
  const [performance, setPerformance] = useState([]);
  const [userDetails, setUserDetails] = useState({});
  const [userScripts, setUserScripts] = useState({});
  const [stockids, setStockids] = useState([]);
  const wsServiceRef = useRef(null);
  const [isRefreshing, setRefreshing] = useState(false);
  const navigate = useNavigate();

  const submitRejectionLog = async (data) => {
    try {
      const payload = typeof data === 'string' ? JSON.parse(data) : data;
      await apiAppFetch('createRejectionLog', {
        method: 'POST',
        body: JSON.stringify(payload),
      });
    } catch (error) {
      // rejection log is best-effort
    }
  };

  const fetchTrades = async () => {
    try {
      const response = await apiAppFetch('getOrders', {
        method: 'POST',
        body: JSON.stringify({}),
      });
      const data = await response.json();
      const sortedData = data.sort((a, b) => b.orderId - a.orderId);
      setTrades(sortedData);
      setRefreshing(false);
    } catch (error) {
      setRefreshing(false);
    }
  };

  const fetchNetPositions = async () => {
    try {
      setRefreshing(true);

      const response = await apiAppFetch('getNetPositionsMain', {
        method: 'POST',
        body: JSON.stringify({}),
      });

      const data = await response.json();

      if (data.success) {
        setNetPositions(data.positions);
        setSummary(data.summary);
      }
    } catch (error) {
      // fetch failed silently
    } finally {
      setRefreshing(false);
    }
  };

  const fetchPerformance = async () => {
    try {
      const response = await apiAppFetch('performanceFromMonday', {
        method: 'POST',
        body: JSON.stringify({}),
      });
      const data = await response.json();

      const list = Array.isArray(data) ? data : [];
      const getOrderId = (item) => item?.closing_order?.orderId ?? item?.orderId ?? 0;
      const sortedData = [...list].sort((a, b) => Number(getOrderId(b)) - Number(getOrderId(a)));

      setPerformance(sortedData);
      setRefreshing(false);
    } catch (error) {
      setRefreshing(false);
    }
  };

  const mergerIDsEx = async (instrumentTokens) => {
    wsServiceRef.current.mergerIDs(instrumentTokens);
  };

  const fetchPortfolio = async () => {
    try {
      const response = await apiAppFetch('portfolio', {
        method: 'POST',
        body: JSON.stringify({}),
      });
      const data = await response.json();

      const instrumentTokens = data.map((script) => script.instrument_token);
      wsServiceRef.current.mergerIDs(instrumentTokens);

      const sortedData = data.sort((a, b) => b.orderId - a.orderId);
      setPortfolio(sortedData);
      setRefreshing(false);
    } catch (error) {
      setRefreshing(false);
    }
  };

  const fetchUserDetails = async () => {
    try {
      const response = await apiAppFetch('getUserDetails', {
        method: 'POST',
        body: JSON.stringify({}),
      });
      const data = await response.json();
      localStorage.setItem('parentId', data.parentId);

      if (data.isActive == 0) {
        localStorage.removeItem('authToken');
        navigate('/login');
      }

      setUserDetails(data);
    } catch (error) {
      // fetch failed silently; UI may retry on refresh
    }
  };

  const fetchUserScripts = async (wsService) => {
    try {
      const response = await apiAppFetch('getUserScriptsWithPrice', {
        method: 'POST',
        body: JSON.stringify({}),
      });

      const data = await response.json();

      setUserScripts(data);

      const instrumentTokens = data.map((script) => script.instrument_token);
      setStockids(instrumentTokens);

      wsServiceRef.current.setIDs(instrumentTokens);
      reconnectWebSocket();

      fetchTrades();
      fetchPortfolio();
      fetchNetPositions();
      fetchPerformance();
      fetchUserDetails();

      setRefreshing(false);
    } catch (error) {
      setRefreshing(false);
    }
  };

  const fetchUserScriptsExternal = () => {
    setRefreshing(true);
    fetchUserScripts(wsServiceRef);
  };

  const removeUserScriptFromList = (instrumentIdentifier, lotVariant) => {
    const idStr = String(instrumentIdentifier ?? '');
    if (!idStr) {
      return;
    }
    const removeMini = String(lotVariant || '').toUpperCase() === 'MINI';

    const list = Array.isArray(userScripts)
      ? userScripts
      : Object.values(userScripts || {});
    const nextScripts = list.filter((script) => {
      if (String(script?.instrumentIdentifier) !== idStr) {
        return true;
      }
      const scriptMini = String(script?.lotVariant || '').toUpperCase() === 'MINI';
      return scriptMini !== removeMini;
    });
    const instrumentTokens = nextScripts
      .map((script) => script.instrument_token)
      .filter(Boolean);

    setUserScripts(nextScripts);
    setStockids(instrumentTokens);

    if (wsServiceRef.current) {
      wsServiceRef.current.setIDs(instrumentTokens);
      reconnectWebSocket();
    }
  };

  useEffect(() => {
    wsServiceRef.current = new WebSocketService(
      resource,
      stockids,
      updateStockData,
      setLastUpdateTime
    );
    fetchUserScripts(wsServiceRef.current);

    return () => {
      wsServiceRef.current.disconnect();
    };
  }, [resource, stockIDs]);

  const updateStockData = (data) => {
    setStocks((prevStocks) => ({
      ...prevStocks,
      [data.Symbol]: data,
    }));
    setLastUpdateTime(new Date());
  };

  const reconnectWebSocket = () => {
    if (wsServiceRef.current) {
      if (wsServiceRef.current.readyState === WebSocket.OPEN) {
        wsServiceRef.current.disconnect();
        wsServiceRef.current.connect();
      } else {
        wsServiceRef.current.connect();
      }
    }
  };

  const refreshTradesAndPortfolio = (refreshType) => {
    try {
      setRefreshing(true);
      if (refreshType === 'trades') {
        fetchTrades();
      } else if (refreshType === 'portfolio') {
        fetchPortfolio();
      } else if (refreshType === 'both') {
        fetchTrades();
        fetchPortfolio();
        fetchPerformance();
        fetchUserDetails();
        fetchNetPositions();
      }
    } catch (error) {
      setRefreshing(false);
    }
  };

  const refreshPerformance = () => {
    setRefreshing(true);
    fetchPerformance();
  };

  return (
    <WebSocketContext.Provider value={{ mergerIDsEx, isRefreshing, stocks, userScripts, lastUpdateTime, trades, positions, performance, userDetails, netPositions, summary, fetchUserScriptsExternal, removeUserScriptFromList, refreshPerformance, fetchUserScripts, refreshTradesAndPortfolio, submitRejectionLog }}>
      {children}
    </WebSocketContext.Provider>
  );
};

export { WebSocketProvider, WebSocketContext };
